// Pure logic extracted from SubsectionBuilderBlock — testable without React or API.

import { SUBSECTION_TEMPLATES } from '../assets/subsectionTemplates';

// Measurement block types that live inside a julkisivut section
export const JULKISIVU_MEASUREMENT_BLOCKS = {
  PeitepaksuusBlock: 'peitepaksuus',
  PoranayteBlock: 'poranäyte',
  VetolujuusBlock: 'vetolujuus',
  OhuthieBlock: 'ohuthie',
  KloridipitoisuusBlock: 'kloridi',
};

/**
 * Walk a section's block tree and inject ptsProjectId + mittausProjektiId
 * into every block whose type is a known measurement block.
 */
export function injectPTSIds(content, ptsProjectId, blockTypeToMittausId) {
  const patch = (blocks) => blocks.map(block => {
    const mittausId = blockTypeToMittausId[block.type];
    if (mittausId !== undefined) {
      return { ...block, data: { ...block.data, ptsProjectId, mittausProjektiId: mittausId } };
    }
    if (block.data?.blocks) {
      return { ...block, data: { ...block.data, blocks: patch(block.data.blocks) } };
    }
    return block;
  });
  return { ...content, blocks: patch(content.blocks ?? []) };
}

/**
 * Replace all template placeholder tags in an object/string with the real chip name.
 * Strips any remaining {{…template…}} sentinels.
 */
export function resolveAllTags(obj, chipName) {
  if (typeof obj === 'string') {
    return obj
      .replaceAll('{{dynamic_subsection_name}}', chipName)
      .replaceAll('{{subsection_name}}', chipName)
      .replaceAll('{{section_name}}', chipName)
      .replace(/\{\{[^}]*template[^}]*\}\}/g, '');
  }
  if (Array.isArray(obj)) return obj.map(item => resolveAllTags(item, chipName));
  if (obj && typeof obj === 'object') {
    return Object.fromEntries(
      Object.entries(obj).map(([k, v]) => [k, resolveAllTags(v, chipName)])
    );
  }
  return obj;
}

/**
 * Auto-set the subCategory on ImageBlocks that have no category yet.
 */
export function applyAutoSubcategory(block, chipName, imageCategory) {
  if (!block || typeof block !== 'object') return block;
  if (Array.isArray(block)) return block.map(b => applyAutoSubcategory(b, chipName, imageCategory));
  if (block.type === 'ImageBlock') {
    const sub = block.data?.subCategory;
    const cat = block.data?.category;
    const needsSub = !sub || sub === 'Kaikki';
    const needsCat = imageCategory && (!cat || cat === '');
    if (needsSub || needsCat) {
      return {
        ...block,
        data: {
          ...block.data,
          ...(needsCat ? { category: imageCategory } : {}),
          ...(needsSub ? { subCategory: chipName, subCategoryIsLocked: true } : {}),
        },
      };
    }
    return block;
  }
  if (block.data?.blocks) {
    return { ...block, data: { ...block.data, blocks: applyAutoSubcategory(block.data.blocks, chipName, imageCategory) } };
  }
  return block;
}

const CHIP_CATEGORY_RANK = Object.fromEntries(SUBSECTION_TEMPLATES.map((t, i) => [t.category, i]));

function getChipRank(section) {
  if (!section?.type?.startsWith('section_chip_')) return null;
  const tmpl = SUBSECTION_TEMPLATES.find(t => `section_chip_${t.category.toLowerCase()}` === section.type);
  return tmpl ? (CHIP_CATEGORY_RANK[tmpl.category] ?? Infinity) : Infinity;
}

/**
 * Insert newSectionId into the section order at the correct position:
 * - After yhteenveto (floor)
 * - Before allekirjoitukset (ceiling)
 * - Among existing chip sections in category rank order
 */
export function buildChipSectionOrder(sections, report, newSectionId, category) {
  const newRank = CHIP_CATEGORY_RANK[category] ?? Infinity;
  const knownIds = new Set(sections.map(s => s.id));
  let orderedIds = Array.isArray(report?.section_order)
    ? report.section_order.filter(id => knownIds.has(id))
    : sections.map(s => s.id);
  const inOrder = new Set(orderedIds);
  sections.forEach(s => { if (!inOrder.has(s.id)) orderedIds.push(s.id); });

  const sectionMap = Object.fromEntries(sections.map(s => [s.id, s]));
  const isYhteenveto     = (s) => s?.type === 'yhteenveto'     || s?.name?.toLowerCase() === 'yhteenveto';
  const isAllekirjoitukset = (s) => s?.type === 'allekirjoitukset' || s?.name?.toLowerCase() === 'allekirjoitukset';

  // Per-report-type: dynamic sections must go after this section type
  const FLOOR_ANCHOR_TYPE = {
    julkisivujen_kuntotutkimus: 'lahtotiedot',
  };

  const floor = (() => {
    const anchorType = FLOOR_ANCHOR_TYPE[report?.type];
    if (anchorType) {
      const idx = orderedIds.reduce((last, id, i) =>
        (sectionMap[id]?.type ?? '').includes(anchorType) ? i : last, -1);
      if (idx !== -1) return idx + 1;
    }
    const indices = orderedIds.map((id, i) => isYhteenveto(sectionMap[id]) ? i : -1).filter(i => i >= 0);
    return indices.length ? Math.max(...indices) + 1 : 0;
  })();
  const ceiling = (() => {
    const indices = orderedIds.map((id, i) => isAllekirjoitukset(sectionMap[id]) ? i : -1).filter(i => i >= 0);
    return indices.length ? Math.min(...indices) : orderedIds.length;
  })();

  let afterSameOrEarlier = -1;
  let beforeLater = -1;
  for (let i = floor; i < ceiling; i++) {
    const rank = getChipRank(sectionMap[orderedIds[i]]);
    if (rank !== null) {
      if (rank <= newRank) afterSameOrEarlier = i + 1;
      else if (beforeLater === -1) beforeLater = i;
    }
  }

  let insertAt;
  if (afterSameOrEarlier !== -1)      insertAt = Math.min(afterSameOrEarlier, ceiling);
  else if (beforeLater !== -1)        insertAt = Math.max(beforeLater, floor);
  else                                insertAt = floor;

  return [...orderedIds.slice(0, insertAt), newSectionId, ...orderedIds.slice(insertAt)];
}

/**
 * Returns a unique project name by appending (2), (3)… if baseName is taken.
 */
export function uniqueProjectName(baseName, existingList) {
  const names = new Set((existingList ?? []).map(p => p.name ?? ''));
  if (!names.has(baseName)) return baseName;
  let n = 2;
  while (names.has(`${baseName} (${n})`)) n++;
  return `${baseName} (${n})`;
}

/**
 * For each measurement block type, find or create a mittausProjekti under linkedProjectId.
 * Re-uses an existing project with the same name to avoid duplicates when a section is
 * removed and re-added. Creates an initial kohde for PoranayteBlock only.
 *
 * @param {number} linkedProjectId - PTS project id
 * @param {string} chipName        - name of the chip / section being created
 * @param {{ getMittausProjektitByPTS, createMittausProjekti, createMittausKohde }} api
 * @returns {Promise<Record<string, number>>} blockType → mittausProjektiId
 */
export async function createMittausProjektitForSection(linkedProjectId, chipName, api) {
  const blockTypeToMittausId = {};
  await Promise.all(
    Object.entries(JULKISIVU_MEASUREMENT_BLOCKS).map(async ([blockType, tyyppi]) => {
      let projId = null;
      try {
        const existing = (await api.getMittausProjektitByPTS(linkedProjectId, tyyppi)).data ?? [];
        const match = existing.find(p => p.name === chipName);
        if (match) projId = match.id;
      } catch {}
      if (!projId) {
        const r = await api.createMittausProjekti(linkedProjectId, tyyppi, chipName);
        projId = r.data.id;
        if (blockType === 'PoranayteBlock') {
          await api.createMittausKohde(projId, 'Rakenneosa 1', 0);
        }
      }
      blockTypeToMittausId[blockType] = projId;
    })
  );
  return blockTypeToMittausId;
}
